import React, { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { NavLink } from "react-router-dom";
import {
  FiArrowDown,
  FiArrowUpRight,
  FiBriefcase,
  FiCheck,
  FiCompass,
  FiDollarSign,
  FiHome,
  FiSettings,
  FiShield,
  FiTruck,
  FiUsers,
} from "react-icons/fi";
import usePrefersReducedMotion from "../../hooks/usePrefersReducedMotion";
import lifeImage from "../../assets/images/slider/life.jpg";
import pecuniaryImage from "../../assets/images/slider/Pecuniary.jpg";
import engineeringImage from "../../assets/images/slider/covered.jpg";
import liabilityImage from "../../assets/images/slider/mum.jpg";
import propertyImage from "../../assets/images/slider/property.jpg";
import travelImage from "../../assets/images/travelIN.jpg";
import motorImage from "../../assets/images/motor-insurance.jpg";
import specialtyImage from "../../assets/images/cargo.jpg";
import "./Products.css";

const products = [
  {
    title: "Life and Personal Insurance",
    category: "Personal",
    image: lifeImage,
    icon: FiUsers,
    description:
      "Protect the people and plans that matter most with cover designed for life's unexpected turns.",
    highlights: ["Key person cover", "Personal protection"],
    link: "/products/life&personal-insurance",
  },
  {
    title: "Pecuniary Insurance",
    category: "Business",
    image: pecuniaryImage,
    icon: FiDollarSign,
    description:
      "Reduce the impact of direct financial losses and keep your business steady through disruption.",
    highlights: ["Financial loss cover", "Business continuity"],
    link: "/products/pecuniary-insurance",
  },
  {
    title: "Engineering Insurance",
    category: "Business",
    image: engineeringImage,
    icon: FiSettings,
    description:
      "Safeguard projects, machinery, and equipment against breakdowns, accidents, and unforeseen damage.",
    highlights: ["Plant and machinery", "Project protection"],
    link: "/products/engineering-insurance",
  },
  {
    title: "Liability Insurance",
    category: "Business",
    image: liabilityImage,
    icon: FiShield,
    description:
      "Protect your organisation when third parties suffer injury or property damage connected to your operations.",
    highlights: ["Third-party claims", "Legal liability"],
    link: "/products/liability-insurance",
  },
  {
    title: "Property Insurance",
    category: "Business",
    image: propertyImage,
    icon: FiHome,
    description:
      "Cover buildings, contents, and essential assets against fire, theft, natural hazards, and other insured risks.",
    highlights: ["Buildings and contents", "Asset protection"],
    link: "/products/property-insurance",
  },
  {
    title: "Travel Insurance",
    category: "Personal",
    image: travelImage,
    icon: FiCompass,
    description:
      "Travel with greater confidence through cover for medical emergencies, financial loss, and lost property.",
    highlights: ["Medical emergencies", "Trip protection"],
    link: "/products/travel-insurance",
  },
  {
    title: "Motor Insurance",
    category: "Personal",
    image: motorImage,
    icon: FiTruck,
    description:
      "Protect your vehicle against accidents, theft, repair costs, and third-party liabilities on the road.",
    highlights: ["Vehicle damage", "Third-party cover"],
    link: "/products/motor-insurance",
  },
  {
    title: "Specialty Lines",
    category: "Specialty",
    image: specialtyImage,
    icon: FiBriefcase,
    description:
      "Specialist protection for complex marine, oil and gas, offshore, and other high-risk operations.",
    highlights: ["Marine risks", "Oil and gas"],
    link: "/products/specialty-lines",
  },
];

const filters = ["All", "Personal", "Business", "Specialty"];

const Products = () => {
  const [activeFilter, setActiveFilter] = useState("All");
  const prefersReducedMotion = usePrefersReducedMotion();

  const visibleProducts = useMemo(
    () =>
      activeFilter === "All"
        ? products
        : products.filter((product) => product.category === activeFilter),
    [activeFilter],
  );

  const motionProps = prefersReducedMotion
    ? {}
    : {
        initial: { opacity: 0, y: 22 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, amount: 0.18 },
        transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
      };

  return (
    <main className="products-page">
      <section className="products-hero">
        <div className="products-shell products-hero-grid">
          <motion.div className="products-hero-copy" {...motionProps}>
            <span className="products-kicker">
              <span aria-hidden="true" />
              Protection that fits
            </span>
            <h1>Cover for the life and business you are building.</h1>
            <p>
              Explore insurance solutions designed to protect your people,
              property, finances, travel, and operations with clarity.
            </p>

            <div className="products-hero-actions">
              <a className="products-primary-action" href="#product-catalogue">
                Explore our products
                <FiArrowDown aria-hidden="true" />
              </a>
              <a className="products-secondary-action" href="/#contact">
                Talk to a broker
                <FiArrowUpRight aria-hidden="true" />
              </a>
            </div>

            <ul className="products-hero-points" aria-label="Product benefits">
              <li>
                <FiCheck aria-hidden="true" />
                Personal and business cover
              </li>
              <li>
                <FiCheck aria-hidden="true" />
                Specialist guidance
              </li>
              <li>
                <FiCheck aria-hidden="true" />
                Clear product options
              </li>
            </ul>
          </motion.div>

          <motion.div
            className="products-hero-visual"
            {...motionProps}
            transition={
              prefersReducedMotion
                ? undefined
                : { duration: 0.65, delay: 0.12, ease: [0.22, 1, 0.36, 1] }
            }
          >
            <div className="products-hero-image">
              <img
                src={propertyImage}
                alt="A modern home representing protected property"
              />
            </div>
            <div className="products-hero-card products-hero-card-top">
              <span>Coverage solutions</span>
              <strong>08</strong>
            </div>
            <div className="products-hero-card products-hero-card-bottom">
              <FiShield aria-hidden="true" />
              <div>
                <strong>Built around you</strong>
                <span>Protection for what matters</span>
              </div>
            </div>
            <div className="products-hero-pattern" aria-hidden="true" />
          </motion.div>
        </div>
      </section>

      <section
        className="products-catalogue"
        id="product-catalogue"
        aria-labelledby="products-heading"
      >
        <div className="products-shell">
          <motion.div className="products-catalogue-header" {...motionProps}>
            <div>
              <span className="products-section-label">Our coverage</span>
              <h2 id="products-heading">Find the protection you need</h2>
              <p>
                Start with a category or browse every solution. Each product
                page explains the available protection in more detail.
              </p>
            </div>

            <div
              className="products-filter"
              role="group"
              aria-label="Filter products by category"
            >
              {filters.map((filter) => (
                <button
                  key={filter}
                  type="button"
                  className={activeFilter === filter ? "active" : ""}
                  onClick={() => setActiveFilter(filter)}
                  aria-pressed={activeFilter === filter}
                >
                  {filter}
                  <span>
                    {filter === "All"
                      ? products.length
                      : products.filter(
                          (product) => product.category === filter,
                        ).length}
                  </span>
                </button>
              ))}
            </div>
          </motion.div>

          <motion.div layout className="products-grid">
            <AnimatePresence mode="popLayout">
              {visibleProducts.map((product, index) => {
                const Icon = product.icon;

                return (
                  <motion.article
                    layout
                    key={product.title}
                    className="product-card"
                    initial={
                      prefersReducedMotion
                        ? false
                        : { opacity: 0, y: 18, scale: 0.98 }
                    }
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={
                      prefersReducedMotion
                        ? undefined
                        : { opacity: 0, y: 10, scale: 0.98 }
                    }
                    transition={{
                      duration: prefersReducedMotion ? 0 : 0.35,
                      delay: prefersReducedMotion ? 0 : index * 0.035,
                    }}
                  >
                    <NavLink
                      className="product-card-link"
                      to={product.link}
                      aria-label={`Explore ${product.title}`}
                    >
                      <div className="product-card-media">
                        <img src={product.image} alt="" />
                        <span className="product-card-category">
                          {product.category}
                        </span>
                        <span className="product-card-icon">
                          <Icon aria-hidden="true" />
                        </span>
                      </div>

                      <div className="product-card-body">
                        <div className="product-card-heading">
                          <h3>{product.title}</h3>
                          <span className="product-card-arrow">
                            <FiArrowUpRight aria-hidden="true" />
                          </span>
                        </div>
                        <p>{product.description}</p>
                        <ul>
                          {product.highlights.map((highlight) => (
                            <li key={highlight}>
                              <FiCheck aria-hidden="true" />
                              {highlight}
                            </li>
                          ))}
                        </ul>
                        <span className="product-card-cta">
                          View coverage details
                        </span>
                      </div>
                    </NavLink>
                  </motion.article>
                );
              })}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      <section className="products-help">
        <motion.div className="products-shell products-help-inner" {...motionProps}>
          <div className="products-help-icon">
            <FiShield aria-hidden="true" />
          </div>
          <div className="products-help-copy">
            <span className="products-section-label">Need some guidance?</span>
            <h2>Not sure which cover is right for you?</h2>
            <p>
              Tell us what you need to protect and our team will help you find
              a suitable place to start.
            </p>
          </div>
          <div className="products-help-actions">
            <a href="/#contact" className="products-primary-action">
              Speak with our team
              <FiArrowUpRight aria-hidden="true" />
            </a>
            <NavLink to="/report-a-claim" className="products-help-link">
              Report a claim
            </NavLink>
          </div>
        </motion.div>
      </section>
    </main>
  );
};

export default Products;
